import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook that tracks which section is currently visible in the viewport.
 * Uses IntersectionObserver with a rootMargin that creates an activation zone
 * in the top portion of the viewport.
 *
 * If the page lives inside a custom scroll container (e.g. AppNav's inner
 * `<div style={{ overflowY: 'auto', height: '100vh' }}>` rather than the
 * window), pass the container element as `root` — without it, the default
 * viewport-rooted observer won't fire because the body doesn't actually
 * scroll in that layout.
 *
 * @param {string[]} sectionIds - Array of DOM element IDs to observe
 * @param {Element|null} [root] - Optional scroll container element
 * @returns {string|null} - The ID of the currently active (most visible) section
 */
export default function useScrollSpy(sectionIds, root = null) {
  const [activeSectionId, setActiveSectionId] = useState(null);
  const observerRef = useRef(null);

  const observe = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // The rootMargin creates a thin activation band in the top ~30% of viewport.
    // When a section's top edge enters this band, it becomes active.
    observerRef.current = new IntersectionObserver(
      entries => {
        // Find the entry that is intersecting and closest to the top
        const intersecting = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (intersecting.length > 0) {
          setActiveSectionId(intersecting[0].target.id);
        }
      },
      {
        root,
        rootMargin: '-10% 0px -70% 0px',
        threshold: 0,
      },
    );

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        observerRef.current.observe(el);
      }
    });
  }, [sectionIds, root]);

  useEffect(() => {
    observe();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [observe]);

  return activeSectionId;
}
